import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Image } from '@tiptap/extension-image';
import { Link as TipTapLink } from '@tiptap/extension-link';
import { Bold } from '@tiptap/extension-bold';
import { Strike } from '@tiptap/extension-strike';
import { Italic } from '@tiptap/extension-italic';
import { Document } from '@tiptap/extension-document';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Text } from '@tiptap/extension-text';
import { generateHTML } from '@tiptap/html';
import parse from 'html-react-parser';

import { useHttp } from 'hooks/http.hook';
import { USEFUL } from 'endpointsConst';
import PageContainer from 'components/common/PageContainer';
import SEO from 'components/common/SEO';
import BackButton from 'components/common/BackButton';
import Loading from '../../Loading';
import { UsefulMaterial } from './types';

import styles from './styles.module.scss';

const UsefulPage: React.FC = () => {
  const { t } = useTranslation();
  const { request } = useHttp();
  // `slug` is what the card links to and what search engines index
  const { slug } = useParams();
  const [material, setMaterial] = useState<UsefulMaterial | null>(null);
  const [isFound, setIsFound] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    request(`${USEFUL}/${slug}`)
      .then((res: any) => {
        setMaterial(res.data);
        setIsFound(true);
        setIsLoading(false);
      })
      .catch(() => {
        setIsFound(false);
        setIsLoading(false);
      });
  }, [slug, request]);

  const bodyHTML = useMemo(() => {
    if (!material?.description) {
      return '<p></p>';
    }

    return generateHTML(material.description as any, [
      Document,
      Paragraph.configure({ HTMLAttributes: { class: styles.bodyParagraph } }),
      Text,
      Bold,
      Italic,
      Strike,
      // Ссылки ведут на внешние источники, поэтому открываем их в новой вкладке
      TipTapLink.configure({
        HTMLAttributes: { class: styles.bodyLink, target: '_blank', rel: 'noopener noreferrer nofollow' },
      }),
      Image.configure({ HTMLAttributes: { class: styles.bodyImage } }),
    ]);
  }, [material?.description]);

  if (isLoading) {
    return (
      <PageContainer>
        <Loading />
      </PageContainer>
    );
  }

  if (!isFound || !material) {
    return (
      <PageContainer>
        <SEO title={t('useful.notFound')} noIndex />
        <div className={styles.notFound}>
          <h1>{t('useful.notFound')}</h1>
          <Link to="/useful">{t('useful.showAll')}</Link>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <SEO
        title={material.title}
        description={material.seo_description || undefined}
        canonicalUrl={`${window.location.origin}/useful/${material.slug}`}
      />

      <article className={styles.article}>
        {/* The design keeps the back button on desktop only */}
        <BackButton className={styles.articleBack} />

        <div className={styles.articleHeader}>
          <h1 className={styles.articleTitle}>{material.title}</h1>
          {material.file && (
            // The file sits on another origin, so `download` is ignored by the browser: a pdf would
            // open in place of the site. A new tab keeps the article where it is, whatever the format
            <a
              className={styles.downloadButton}
              href={material.file}
              download={material.file_name || undefined}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('useful.download')}
            </a>
          )}
        </div>

        <div className={styles.articleBody}>{parse(bodyHTML)}</div>
      </article>
    </PageContainer>
  );
};

export default UsefulPage;
